import type {
  FieldMetadata,
  IFormData,
  MetadataForEdit,
} from "~/components/planning/MultiEditDialog/types"

const toComparable = (val: unknown): unknown => {
  if (val instanceof Date) return val.getTime()
  return val
}

const lifecycleDeterminants = [
  "lifecycleStatus",
  "activatedUntil",
  "phasingOutFrom",
  "activatedFrom",
] as const

const badgeableSources = ["Custom", "ERP", "Default"] as const

const hasBadgeableSource = (field: FieldMetadata | undefined) =>
  field?.source != null && badgeableSources.includes(field.source)

export const pickLifecycleBadgeField = (
  formData: IFormData,
  metadataForEdit: MetadataForEdit,
  isFieldChanged: (field: keyof IFormData) => boolean,
): keyof IFormData =>
  lifecycleDeterminants.find(isFieldChanged) ??
  lifecycleDeterminants.find(
    (field) => formData[field] != null && hasBadgeableSource(metadataForEdit.fields[field]),
  ) ??
  lifecycleDeterminants.find((field) => hasBadgeableSource(metadataForEdit.fields[field])) ??
  "lifecycleStatus"

export const hasFieldChanged = <K extends keyof IFormData>(
  field: K,
  formData: IFormData,
  originalFormData: IFormData,
): boolean => {
  const currentValue = formData[field]
  const originalValue = originalFormData[field]

  const normalizedCurrent = currentValue === "" ? undefined : currentValue
  const normalizedOriginal = originalValue === "" ? undefined : originalValue

  return toComparable(normalizedCurrent) !== toComparable(normalizedOriginal)
}

export const normalizeMultipleValue = <T>(value: T | "Multiple values"): T | undefined => {
  return value === "Multiple values" ? undefined : value
}

/**
 * Where the reset button puts a field. A saved custom value drops back to ERP (or the
 * company default); an ERP or default value the user has only edited in the dialog goes
 * back to what the field held when the dialog opened.
 */
export const getResetValue = <K extends keyof IFormData>(
  field: K,
  originalFormData: IFormData,
  metadataForEdit: MetadataForEdit | undefined,
): IFormData[K] | null | undefined => {
  const fieldData = metadataForEdit?.fields[field]
  if (fieldData?.source !== "Custom") return originalFormData[field]

  return (
    normalizeMultipleValue(fieldData.metadata?.erpValue) ??
    normalizeMultipleValue(fieldData.metadata?.defaultValue) ??
    null
  )
}

export const shouldShowResetButton = <K extends keyof IFormData>(
  field: K,
  formData: IFormData,
  originalFormData: IFormData,
  metadataForEdit: MetadataForEdit | undefined,
): boolean => {
  const fieldData = metadataForEdit?.fields[field]
  if (!fieldData?.isEditable) return false

  if (fieldData.source !== "Custom") return hasFieldChanged(field, formData, originalFormData)
  if (fieldData.isMultiple) return true

  return (
    toComparable(formData[field]) !==
    toComparable(getResetValue(field, originalFormData, metadataForEdit))
  )
}

/**
 * The buffer type the dialog is presenting to the user, regardless of whether they
 * touched the type select: form value → aggregated field value (when not mixed) →
 * company default → "noSafetyStock". A bufferValue is meaningless without this type,
 * so bulk saves must persist the pair together.
 */
export const getEffectiveBufferType = (
  formData: IFormData,
  metadataForEdit: MetadataForEdit | undefined,
): string => {
  const bufferTypeField = metadataForEdit?.fields.bufferType
  const fieldValue = bufferTypeField?.value
  return (
    formData.bufferType ??
    (fieldValue !== "Multiple values" ? fieldValue : undefined) ??
    bufferTypeField?.metadata?.defaultValue ??
    "noSafetyStock"
  )
}

export const getFieldValueForSave = <K extends keyof IFormData>(
  field: K,
  formData: IFormData,
  originalFormData: IFormData,
  metadataForEdit: MetadataForEdit | undefined,
): IFormData[K] | null | undefined => {
  if (!hasFieldChanged(field, formData, originalFormData)) {
    return undefined
  }

  const value = formData[field]
  if (value === "" || value === undefined) {
    return null as IFormData[K]
  }

  const fieldData = metadataForEdit?.fields[field]
  const erpValue = fieldData?.metadata?.erpValue
  const defaultValue = fieldData?.metadata?.defaultValue

  // Clear metadata if value matches ERP value (reset to ERP)
  if (erpValue !== undefined && erpValue !== null) {
    if (toComparable(value) === toComparable(erpValue)) {
      return null as IFormData[K]
    }
  }

  if (
    defaultValue !== undefined &&
    defaultValue !== null &&
    toComparable(value) === toComparable(defaultValue)
  ) {
    return null as IFormData[K]
  }

  return value
}
